import {
  Box,
  Checkbox,
  FieldLabel,
  FieldMessage,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import type { ComponentProps, FormEvent } from 'react';

interface Props {
  id: string;
  label: string;
  columns?: ComponentProps<typeof Tiles>['columns'];
  onChange: any;
  value: string[];
  options: Array<
    | { value: string; badge: React.ComponentProps<typeof Checkbox>['badge'] }
    | string
  >;
  error: boolean;
  description?: string;
}

export const CheckboxGroup = ({
  id,
  label,
  columns,
  onChange,
  value,
  options,
  error,
  description,
}: Props) => {
  const checkboxes = options.map((item, index) => {
    const itemValue = typeof item === 'string' ? item : item.value;
    const itemBadge = typeof item === 'string' ? undefined : item.badge;

    const checked = value.includes(itemValue);

    const handleChange = (e: FormEvent<HTMLInputElement>) => {
      if (checked) {
        const newValues = value.filter(
          (i) => i !== (e.target as HTMLInputElement).value,
        );
        onChange([...newValues]);
      } else {
        onChange([...value, (e.target as HTMLInputElement).value]);
      }
    };

    return (
      <Checkbox
        key={index}
        id={`checkbox-${itemValue.toLowerCase()}`}
        checked={checked}
        onChange={handleChange}
        label={itemValue}
        value={itemValue}
        badge={itemBadge}
      />
    );
  });

  return (
    <Box paddingY="small">
      <Stack space="medium">
        <FieldLabel htmlFor={id} label={label} />
        {Boolean(description) && <Text>{description}</Text>}
        <Box
          id={id}
          paddingLeft="gutter"
          borderRadius="standard"
          boxShadow={error ? 'borderCritical' : undefined}
          paddingY={error ? 'gutter' : 'none'}
        >
          {columns ? (
            <Tiles columns={columns} space="gutter">
              {checkboxes}
            </Tiles>
          ) : (
            <Stack space="medium">{checkboxes}</Stack>
          )}
          {error ? (
            <Box paddingTop="medium">
              <FieldMessage id={id} message="Required Field" tone="critical" />
            </Box>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
};
